// src/components/admin/flows/WorkflowBuilder.js
import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
    ArrowLeft, Save, CheckCircle, Play, AlertTriangle, ChevronDown
} from 'lucide-react';
import { useFlowAutomation, generateId } from '../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { FlowAutomationProvider } from '../../../contexts/FlowAutomationContext';
import WorkflowCanvas from './canvas/WorkflowCanvas';
import { NODE_TYPES, getDefaultConfig } from './canvas/nodeRegistry';
import NodeConfigPanel from './panels/NodeConfigPanel';

const PALETTE_ITEMS = Object.values(NODE_TYPES).filter(t => t.type !== 'trigger'); // Trigger is auto-added

const BuilderContent = () => {
    const { workflowId } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [saving, setSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [showNameEdit, setShowNameEdit] = useState(false);

    const {
        currentWorkflow,
        selectedNodeId,
        isDirty,
        loading,
        fetchWorkflow,
        initNewWorkflow,
        saveWorkflow,
        addStep,
        updateWorkflowField,
        saveDraft,
    } = useFlowAutomation();

    // Detect invalid UUID up-front (before any effects run). This avoids render loops.
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const isInvalidUuid = workflowId && workflowId !== 'new' && !uuidRegex.test(workflowId);

    // Track not-found state (for valid UUID but missing workflow)
    const [notFound, setNotFound] = useState(false);

    // Load existing workflow or init new
    useEffect(() => {
        if (isInvalidUuid) return;

        if (workflowId && workflowId !== 'new') {
            fetchWorkflow(workflowId).then(result => {
                if (!result) setNotFound(true);
            });
        } else if (!currentWorkflow) {
            initNewWorkflow();
        }
    }, [workflowId, isInvalidUuid]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-save draft every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            if (isDirty) saveDraft();
        }, 30000);
        return () => clearInterval(interval);
    }, [isDirty, saveDraft]);

    const handleSave = async () => {
        setSaving(true);
        const result = await saveWorkflow();
        setSaving(false);
        if (result?.workflow?.id && !workflowId) {
            navigate(`/admin/flows/builder/${result.workflow.id}`, { replace: true });
        }
    };

    const handleValidate = () => {
        const errors = [];
        if (!currentWorkflow) return;

        const { steps, connections } = currentWorkflow;
        const triggers = steps.filter(s => s.type === 'trigger');
        const ends = steps.filter(s => s.type === 'end');

        if (triggers.length === 0) errors.push('Workflow must have a trigger');
        if (ends.length === 0) errors.push('Workflow should have an end node');

        // Check disconnected nodes
        steps.forEach(step => {
            if (step.type === 'trigger') return;
            const hasInput = connections.some(c => c.to_step_id === step.id);
            if (!hasInput) errors.push(`"${step.label}" has no incoming connection`);
        });

        steps.forEach(step => {
            if (step.type === 'end') return;
            const hasOutput = connections.some(c => c.from_step_id === step.id);
            if (!hasOutput) errors.push(`"${step.label}" has no outgoing connection`);
        });

        setValidationErrors(errors);
    };

    // Track next Y position via ref to handle rapid clicks correctly
    const nextYRef = useRef(200);
    useEffect(() => {
        if (currentWorkflow?.steps?.length) {
            const maxY = currentWorkflow.steps.reduce((max, s) => Math.max(max, s.position.y), 0);
            nextYRef.current = maxY + 130;
        }
    }, [currentWorkflow?.steps?.length]);

    const handleAddNode = useCallback((type) => {
        const y = nextYRef.current;
        nextYRef.current = y + 130;

        addStep({
            id: generateId('step'),
            type,
            label: NODE_TYPES[type]?.label || type,
            config: getDefaultConfig(type),
            position: { x: 300, y },
        });
    }, [addStep]);

    // Redirect on invalid UUID or not found (all hooks already called above)
    if (isInvalidUuid || notFound) {
        return <Navigate to="/admin/flows?tab=workflows" replace />;
    }

    if (loading && !currentWorkflow) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-120px)] flex flex-col -m-6">
            {/* Toolbar */}
            <div className={`flex items-center justify-between px-4 py-3 border-b ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/admin/flows?tab=workflows')}
                        className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>

                    {showNameEdit ? (
                        <input
                            autoFocus
                            value={currentWorkflow?.name || ''}
                            onChange={(e) => updateWorkflowField('name', e.target.value)}
                            onBlur={() => setShowNameEdit(false)}
                            onKeyDown={(e) => e.key === 'Enter' && setShowNameEdit(false)}
                            className={`text-lg font-semibold px-2 py-1 rounded border ${
                                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            } focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                        />
                    ) : (
                        <button
                            onClick={() => setShowNameEdit(true)}
                            className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} hover:underline`}
                        >
                            {currentWorkflow?.name || 'Untitled Workflow'}
                        </button>
                    )}

                    {isDirty && (
                        <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-600'}`}>
                            Unsaved
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleValidate}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm ${
                            isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        }`}
                    >
                        <CheckCircle className="w-4 h-4" />
                        Validate
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {/* Validation errors */}
            {validationErrors.length > 0 && (
                <div className={`mx-4 mt-3 p-3 rounded-lg border ${isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">Validation Issues</span>
                    </div>
                    <ul className="space-y-0.5 ml-6">
                        {validationErrors.map((err, i) => (
                            <li key={i} className="text-xs text-red-600 dark:text-red-400 list-disc">{err}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Builder Area */}
            <div className="flex-1 flex overflow-hidden">
                {/* Node Palette (Left Sidebar) */}
                <div className={`w-56 flex-shrink-0 border-r overflow-y-auto p-3 space-y-1.5 ${
                    isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                    <p className={`text-xs font-semibold uppercase tracking-wider mb-3 px-1 ${
                        isDark ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                        Add Nodes
                    </p>
                    {PALETTE_ITEMS.map((nodeType) => {
                        const Icon = nodeType.icon;
                        return (
                            <button
                                key={nodeType.type}
                                onClick={() => handleAddNode(nodeType.type)}
                                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors ${
                                    isDark
                                        ? 'hover:bg-gray-700 text-gray-300'
                                        : 'hover:bg-gray-200 text-gray-700'
                                }`}
                            >
                                <div
                                    className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: nodeType.color + '20' }}
                                >
                                    <Icon className="w-4 h-4" style={{ color: nodeType.color }} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium">{nodeType.label}</p>
                                    <p className={`text-[10px] truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {nodeType.description}
                                    </p>
                                </div>
                            </button>
                        );
                    })}

                    <div className={`border-t pt-3 mt-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 px-1 ${
                            isDark ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                            Workflow Type
                        </p>
                        <select
                            value={currentWorkflow?.type || 'custom'}
                            onChange={(e) => updateWorkflowField('type', e.target.value)}
                            className={`w-full px-3 py-2 rounded-lg text-sm border ${
                                isDark
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            <option value="custom">Custom</option>
                            <option value="onboarding">Onboarding</option>
                            <option value="offboarding">Offboarding</option>
                            <option value="approval_chain">Approval Chain</option>
                            <option value="task_automation">Task Automation</option>
                        </select>
                    </div>
                </div>

                {/* Canvas */}
                <WorkflowCanvas />

                {/* Config Panel (Right Sidebar) */}
                {selectedNodeId && <NodeConfigPanel />}
            </div>
        </div>
    );
};

// Wrap with provider if not already wrapped
const WorkflowBuilder = () => {
    return (
        <FlowAutomationProvider>
            <BuilderContent />
        </FlowAutomationProvider>
    );
};

export default WorkflowBuilder;
